import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { Box, Divider, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import Chart from '@/components/general/Chart';
import Icon from '@/components/general/Icon';
import { Placeholders } from '@/types';
import { numberToPercent } from '@/utils/format';
import styles from './SubmittedCheckinsReport.styles';

interface SubmittedCheckinsReportComponentProps {
  labels: string[];
  averageByWeek: number[];
  totalAverage: number;
  totalAverageColorIndicator: string;
  totalSubmitted: number;
  totalSubmittedOnTime: number;
  averageVariation: number;
  averageVariationSinceDate: string;
  isAverageVariationPositive: boolean;
}

const SubmittedCheckinsReportComponent = function ({
  labels,
  averageByWeek,
  totalAverage,
  totalAverageColorIndicator,
  totalSubmitted,
  totalSubmittedOnTime,
  averageVariation,
  averageVariationSinceDate,
  isAverageVariationPositive,
}: SubmittedCheckinsReportComponentProps): JSX.Element {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid item md={12}>
      <Chart
        labels={labels}
        isPercentChart
        data={averageByWeek}
        title="Submitted Check-ins"
        tooltipLabel="Average submitted check-ins"
        lineWithColorIndicator
        colorIndicator={totalAverageColorIndicator}
        data-testid="submitted-checkins-chart"
      >
        <Box
          marginTop={2}
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={styles.detailsContainer}
        >
          <Box>
            <Typography color="primary">Avg submission rate</Typography>

            {totalAverage && averageVariationSinceDate ? (
              <>
                <Typography
                  color="primary.dark"
                  fontWeight={700}
                  fontSize={30}
                  data-testid="total-submitted-checkins-average"
                >
                  {numberToPercent(totalAverage)}
                </Typography>

                <Typography
                  color="primary"
                  fontSize={14}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <Icon
                    size={14}
                    icon={isAverageVariationPositive ? faArrowUp : faArrowDown}
                    color={isAverageVariationPositive ? 'auxGreen' : 'auxRed'}
                  />
                  <Typography
                    marginX={0.5}
                    fontWeight={600}
                    color={isAverageVariationPositive ? 'auxGreen.main' : 'auxRed.main'}
                    data-testid="submitted-checkins-average-variation"
                  >
                    {averageVariation ? numberToPercent(averageVariation, false) : '0%'}
                  </Typography>{' '}
                  since {averageVariationSinceDate}
                </Typography>
              </>
            ) : (
              <Typography color="primary" fontWeight={600} component="span">
                {Placeholders.NO_FILTERED_DATA}
              </Typography>
            )}
          </Box>

          <Divider
            orientation={isMobileScreen ? 'horizontal' : 'vertical'}
            sx={{ marginY: { xs: 3, sm: 0 } }}
            flexItem
          />

          <Box>
            <Typography color="primary">
              <strong data-testid="total-checkins-submitted">{totalSubmitted}</strong> submitted
              Check-ins
            </Typography>

            <Typography color="primary" marginTop={1}>
              <strong data-testid="total-checkins-submitted-on-time">
                {numberToPercent(totalSubmittedOnTime / totalSubmitted)}
              </strong>{' '}
              submitted on time
            </Typography>
          </Box>
        </Box>
      </Chart>
    </Grid>
  );
};

export default SubmittedCheckinsReportComponent;
